/**
 * BCP-47 locale catalogue for the translations picker.
 *
 * Curated list of the locales we actually expect customers to ask
 * for — the ~150 most-used languages plus the regional variants that
 * differ in spelling / script (en-US vs en-GB, pt-BR vs pt-PT,
 * zh-Hans vs zh-Hant, etc.). Each entry carries an English label and
 * the endonym so the Autocomplete search matches both
 * ("Dutch" → nl, "Nederlands" → nl).
 *
 * Not exhaustive on purpose — Autocomplete also accepts free text
 * for the long tail (the API doesn't validate locale codes against
 * this list).
 */

const LOCALES = [
  { code: 'af', label: 'Afrikaans', native: 'Afrikaans' },
  { code: 'sq', label: 'Albanian', native: 'Shqip' },
  { code: 'am', label: 'Amharic', native: 'አማርኛ' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
  { code: 'ar-EG', label: 'Arabic (Egypt)', native: 'العربية (مصر)' },
  { code: 'ar-SA', label: 'Arabic (Saudi Arabia)', native: 'العربية (السعودية)' },
  { code: 'hy', label: 'Armenian', native: 'Հայերեն' },
  { code: 'az', label: 'Azerbaijani', native: 'Azərbaycan' },
  { code: 'eu', label: 'Basque', native: 'Euskara' },
  { code: 'be', label: 'Belarusian', native: 'Беларуская' },
  { code: 'bn', label: 'Bengali', native: 'বাংলা' },
  { code: 'bs', label: 'Bosnian', native: 'Bosanski' },
  { code: 'bg', label: 'Bulgarian', native: 'Български' },
  { code: 'my', label: 'Burmese', native: 'မြန်မာ' },
  { code: 'ca', label: 'Catalan', native: 'Català' },
  { code: 'ceb', label: 'Cebuano', native: 'Cebuano' },
  { code: 'zh-Hans', label: 'Chinese (Simplified)', native: '简体中文' },
  { code: 'zh-Hant', label: 'Chinese (Traditional)', native: '繁體中文' },
  { code: 'zh-HK', label: 'Chinese (Hong Kong)', native: '中文 (香港)' },
  { code: 'co', label: 'Corsican', native: 'Corsu' },
  { code: 'hr', label: 'Croatian', native: 'Hrvatski' },
  { code: 'cs', label: 'Czech', native: 'Čeština' },
  { code: 'da', label: 'Danish', native: 'Dansk' },
  { code: 'nl', label: 'Dutch', native: 'Nederlands' },
  { code: 'nl-BE', label: 'Dutch (Belgium)', native: 'Nederlands (België)' },
  { code: 'en', label: 'English', native: 'English' },
  { code: 'en-US', label: 'English (United States)', native: 'English (US)' },
  { code: 'en-GB', label: 'English (United Kingdom)', native: 'English (UK)' },
  { code: 'en-AU', label: 'English (Australia)', native: 'English (AU)' },
  { code: 'en-CA', label: 'English (Canada)', native: 'English (CA)' },
  { code: 'en-IN', label: 'English (India)', native: 'English (IN)' },
  { code: 'en-NZ', label: 'English (New Zealand)', native: 'English (NZ)' },
  { code: 'en-SG', label: 'English (Singapore)', native: 'English (SG)' },
  { code: 'en-ZA', label: 'English (South Africa)', native: 'English (ZA)' },
  { code: 'eo', label: 'Esperanto', native: 'Esperanto' },
  { code: 'et', label: 'Estonian', native: 'Eesti' },
  { code: 'fil', label: 'Filipino', native: 'Filipino' },
  { code: 'fi', label: 'Finnish', native: 'Suomi' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'fr-FR', label: 'French (France)', native: 'Français (France)' },
  { code: 'fr-CA', label: 'French (Canada)', native: 'Français (Canada)' },
  { code: 'fr-BE', label: 'French (Belgium)', native: 'Français (Belgique)' },
  { code: 'fr-CH', label: 'French (Switzerland)', native: 'Français (Suisse)' },
  { code: 'fy', label: 'Frisian', native: 'Frysk' },
  { code: 'gl', label: 'Galician', native: 'Galego' },
  { code: 'ka', label: 'Georgian', native: 'ქართული' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'de-DE', label: 'German (Germany)', native: 'Deutsch (Deutschland)' },
  { code: 'de-AT', label: 'German (Austria)', native: 'Deutsch (Österreich)' },
  { code: 'de-CH', label: 'German (Switzerland)', native: 'Deutsch (Schweiz)' },
  { code: 'el', label: 'Greek', native: 'Ελληνικά' },
  { code: 'gu', label: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'ht', label: 'Haitian Creole', native: 'Kreyòl Ayisyen' },
  { code: 'ha', label: 'Hausa', native: 'Hausa' },
  { code: 'haw', label: 'Hawaiian', native: 'ʻŌlelo Hawaiʻi' },
  { code: 'he', label: 'Hebrew', native: 'עברית' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'hmn', label: 'Hmong', native: 'Hmoob' },
  { code: 'hu', label: 'Hungarian', native: 'Magyar' },
  { code: 'is', label: 'Icelandic', native: 'Íslenska' },
  { code: 'ig', label: 'Igbo', native: 'Igbo' },
  { code: 'id', label: 'Indonesian', native: 'Bahasa Indonesia' },
  { code: 'ga', label: 'Irish', native: 'Gaeilge' },
  { code: 'it', label: 'Italian', native: 'Italiano' },
  { code: 'ja', label: 'Japanese', native: '日本語' },
  { code: 'jv', label: 'Javanese', native: 'Basa Jawa' },
  { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'kk', label: 'Kazakh', native: 'Қазақ' },
  { code: 'km', label: 'Khmer', native: 'ខ្មែរ' },
  { code: 'rw', label: 'Kinyarwanda', native: 'Kinyarwanda' },
  { code: 'ko', label: 'Korean', native: '한국어' },
  { code: 'ku', label: 'Kurdish', native: 'Kurdî' },
  { code: 'ky', label: 'Kyrgyz', native: 'Кыргызча' },
  { code: 'lo', label: 'Lao', native: 'ລາວ' },
  { code: 'la', label: 'Latin', native: 'Latina' },
  { code: 'lv', label: 'Latvian', native: 'Latviešu' },
  { code: 'lt', label: 'Lithuanian', native: 'Lietuvių' },
  { code: 'lb', label: 'Luxembourgish', native: 'Lëtzebuergesch' },
  { code: 'mk', label: 'Macedonian', native: 'Македонски' },
  { code: 'mg', label: 'Malagasy', native: 'Malagasy' },
  { code: 'ms', label: 'Malay', native: 'Bahasa Melayu' },
  { code: 'ml', label: 'Malayalam', native: 'മലയാളം' },
  { code: 'mt', label: 'Maltese', native: 'Malti' },
  { code: 'mi', label: 'Maori', native: 'Māori' },
  { code: 'mr', label: 'Marathi', native: 'मराठी' },
  { code: 'mn', label: 'Mongolian', native: 'Монгол' },
  { code: 'ne', label: 'Nepali', native: 'नेपाली' },
  { code: 'no', label: 'Norwegian', native: 'Norsk' },
  { code: 'nb', label: 'Norwegian (Bokmål)', native: 'Norsk Bokmål' },
  { code: 'nn', label: 'Norwegian (Nynorsk)', native: 'Nynorsk' },
  { code: 'ny', label: 'Nyanja (Chichewa)', native: 'Chichewa' },
  { code: 'or', label: 'Odia', native: 'ଓଡ଼ିଆ' },
  { code: 'ps', label: 'Pashto', native: 'پښتو' },
  { code: 'fa', label: 'Persian', native: 'فارسی' },
  { code: 'pl', label: 'Polish', native: 'Polski' },
  { code: 'pt', label: 'Portuguese', native: 'Português' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)', native: 'Português (Brasil)' },
  { code: 'pt-PT', label: 'Portuguese (Portugal)', native: 'Português (Portugal)' },
  { code: 'pa', label: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'ro', label: 'Romanian', native: 'Română' },
  { code: 'ru', label: 'Russian', native: 'Русский' },
  { code: 'sm', label: 'Samoan', native: 'Gagana Sāmoa' },
  { code: 'gd', label: 'Scots Gaelic', native: 'Gàidhlig' },
  { code: 'sr', label: 'Serbian', native: 'Српски' },
  { code: 'sr-Latn', label: 'Serbian (Latin)', native: 'Srpski' },
  { code: 'st', label: 'Sesotho', native: 'Sesotho' },
  { code: 'sn', label: 'Shona', native: 'Shona' },
  { code: 'sd', label: 'Sindhi', native: 'سنڌي' },
  { code: 'si', label: 'Sinhala', native: 'සිංහල' },
  { code: 'sk', label: 'Slovak', native: 'Slovenčina' },
  { code: 'sl', label: 'Slovenian', native: 'Slovenščina' },
  { code: 'so', label: 'Somali', native: 'Soomaali' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'es-ES', label: 'Spanish (Spain)', native: 'Español (España)' },
  { code: 'es-MX', label: 'Spanish (Mexico)', native: 'Español (México)' },
  { code: 'es-AR', label: 'Spanish (Argentina)', native: 'Español (Argentina)' },
  { code: 'es-CO', label: 'Spanish (Colombia)', native: 'Español (Colombia)' },
  { code: 'es-CL', label: 'Spanish (Chile)', native: 'Español (Chile)' },
  { code: 'es-419', label: 'Spanish (Latin America)', native: 'Español (Latinoamérica)' },
  { code: 'su', label: 'Sundanese', native: 'Basa Sunda' },
  { code: 'sw', label: 'Swahili', native: 'Kiswahili' },
  { code: 'sv', label: 'Swedish', native: 'Svenska' },
  { code: 'tg', label: 'Tajik', native: 'Тоҷикӣ' },
  { code: 'ta', label: 'Tamil', native: 'தமிழ்' },
  { code: 'tt', label: 'Tatar', native: 'Татарча' },
  { code: 'te', label: 'Telugu', native: 'తెలుగు' },
  { code: 'th', label: 'Thai', native: 'ไทย' },
  { code: 'tr', label: 'Turkish', native: 'Türkçe' },
  { code: 'tk', label: 'Turkmen', native: 'Türkmen' },
  { code: 'uk', label: 'Ukrainian', native: 'Українська' },
  { code: 'ur', label: 'Urdu', native: 'اردو' },
  { code: 'ug', label: 'Uyghur', native: 'ئۇيغۇرچە' },
  { code: 'uz', label: 'Uzbek', native: 'Oʻzbek' },
  { code: 'vi', label: 'Vietnamese', native: 'Tiếng Việt' },
  { code: 'cy', label: 'Welsh', native: 'Cymraeg' },
  { code: 'xh', label: 'Xhosa', native: 'isiXhosa' },
  { code: 'yi', label: 'Yiddish', native: 'ייִדיש' },
  { code: 'yo', label: 'Yoruba', native: 'Yorùbá' },
  { code: 'zu', label: 'Zulu', native: 'isiZulu' },
];

export default LOCALES;

export function findLocale(code) {
  if (!code) return null;
  const lower = code.toLowerCase();
  return LOCALES.find((l) => l.code.toLowerCase() === lower) || null;
}

export function localeDisplayName(code) {
  const hit = findLocale(code);
  if (!hit) {
    // A code the list does not carry, such as a bare `zh` an API or MCP
    // client stored: the browser's own name for it beats the raw code.
    try {
      const name = new Intl.DisplayNames(['en'], { type: 'language' }).of(code);
      return name && name !== code ? `${name} (${code})` : code;
    } catch {
      return code || '';
    }
  }
  return hit.native === hit.label ? hit.label : `${hit.label} (${hit.native})`;
}
